import React, { useEffect, useRef, useState } from 'react';
import { Shield, TrendingUp, Handshake } from 'lucide-react';

const AlignedIncentives = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    const headerObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisibleItems(p => [...new Set([...p, 'header'])]); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) headerObs.observe(sectionRef.current);

    const cardObs = cardRefs.current.map((ref, i) => {
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) setTimeout(() => setVisibleItems(p => [...new Set([...p, i])]), i * 150);
        },
        { threshold: 0.15 }
      );
      if (ref) obs.observe(ref);
      return obs;
    });

    const bottomObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisibleItems(p => [...new Set([...p, 'bottom'])]); },
      { threshold: 0.1 }
    );
    if (bottomRef.current) bottomObs.observe(bottomRef.current);

    return () => { headerObs.disconnect(); cardObs.forEach(o => o.disconnect()); bottomObs.disconnect(); };
  }, []);

  const benefits = [
    {
      icon: Handshake,
      title: "Performance-Based Partnership",
      description: "This is not an agency package. The incentive is simple: the product has to sell."
    },
    {
      icon: TrendingUp,
      title: "If the Product Makes Money",
      description: "We both win. Our success is directly tied to your success."
    },
    {
      icon: Shield,
      title: "If It Doesn't Sell",
      description: "There's no financial downside. You invested time, not capital."
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-[rgb(17,17,19)] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[rgba(197,155,47,0.06)] blur-[120px] rounded-full pointer-events-none"></div>

      <div className="max-w-[1100px] mx-auto relative z-10">
        <div className={`text-center mb-16 transition-all duration-700 ${
          visibleItems.includes('header') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Aligned Incentives.</span> Zero Risk.
          </h2>
          <p className="text-xl text-[rgb(218,218,218)] max-w-[700px] mx-auto">
            We only succeed when you succeed — that's the partnership
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div
                key={index}
                ref={el => (cardRefs.current[index] = el)}
                className={`bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 hover:border-[rgb(197,155,47)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(197,155,47,0.2)] group ${
                  visibleItems.includes(index)
                    ? 'opacity-100 translate-y-0 scale-100'
                    : 'opacity-0 translate-y-12 scale-90'
                }`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[rgba(197,155,47,0.2)] to-[rgba(124,56,178,0.2)] border border-[rgba(197,155,47,0.3)] flex items-center justify-center mb-6 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(197,155,47,0.3)] transition-all duration-300">
                  <Icon size={28} className="text-[rgb(197,155,47)]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[rgb(197,155,47)] transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="text-[rgb(161,161,170)] leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            );
          })}
        </div>

        <div
          ref={bottomRef}
          className={`bg-gradient-to-br from-[rgba(197,155,47,0.15)] to-[rgba(124,56,178,0.15)] border-2 border-[rgb(197,155,47)] rounded-2xl p-10 text-center relative overflow-hidden transition-all duration-700 ${
            visibleItems.includes('bottom') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
          }`}
        >
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[rgb(124,56,178)] via-[rgb(197,155,47)] to-[rgb(124,56,178)]"></div>
          <p className="text-2xl md:text-3xl font-bold text-white mb-4">
            Your investment is <span className="text-[rgb(197,155,47)]">time and trust</span>, not money
          </p>
          <p className="text-lg text-[rgb(218,218,218)] max-w-[600px] mx-auto">
            We take on the financial risk because we believe in creating products that actually sell
          </p>
        </div>
      </div>
    </section>
  );
};

export default AlignedIncentives;